// src/features/staff/pages/StaffPerformancePage.tsx
// FULLY REDESIGNED PERFORMANCE PAGE

import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { ErrorState } from '@/components/ui/ErrorState';
import { EmptyState } from '@/components/ui/EmptyState';
import * as staffApi from '../services/staffApi';
import * as businessApi from '@/features/business/services/businessApi';
import type { StaffPerformanceMetrics, PerformanceResponse } from '../services/staffApi';
import type { Business } from '@/features/business/services/businessApi';
import {
    ArrowLeft, Users, TrendingUp, DollarSign,
    Trophy, Medal, ShoppingCart,
    Clock,
    Store, Search, Crown, Sparkles,
    ChevronLeft, ChevronRight, LayoutGrid,
    List, ArrowUpDown, X, TrendingUp as TrendingUpIcon,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { clsx } from 'clsx';

// ============================================================
// FORMATTERS
// ============================================================

const formatNaira = (val: string | number) => {
    const num = typeof val === 'string' ? parseFloat(val) : val;
    if (isNaN(num)) return '₦0.00';
    if (num >= 1000000) return `₦${(num / 1000000).toFixed(2)}M`;
    if (num >= 1000) return `₦${(num / 1000).toFixed(1)}K`;
    return `₦${num.toLocaleString('en-NG', { minimumFractionDigits: 2 })}`;
};

const formatNumber = (val: number) => {
    if (!val && val !== 0) return '0';
    return val.toLocaleString('en-NG');
};

// ============================================================
// RANK BADGE COMPONENT
// ============================================================

function RankBadge({ rank }: { rank: number }) {
    if (!rank || rank <= 0) return <span className="text-xs font-semibold text-slate-400">—</span>;
    if (rank === 1) return (
        <div className="relative inline-flex items-center justify-center">
            <Crown size={22} className="text-amber-400 drop-shadow-md" />
            <Sparkles size={10} className="absolute -top-1 -right-1 text-amber-300" />
        </div>
    );
    if (rank === 2) return <Medal size={20} className="text-slate-400 drop-shadow-sm inline-block" />;
    if (rank === 3) return <Medal size={20} className="text-amber-600 drop-shadow-sm inline-block" />;
    return (
        <div className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center mx-auto">
            <span className="text-[11px] font-bold text-slate-500">{rank}</span>
        </div>
    );
}

// ============================================================
// PERFORMANCE BAR
// ============================================================

function PerformanceBar({ value, max, color, label }: { value: number; max: number; color: string; label?: string }) {
    const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
    return (
        <div className="space-y-1">
            {label && (
                <div className="flex justify-between text-[10px]">
                    <span className="text-slate-400">{label}</span>
                    <span className="font-bold text-slate-600">{pct.toFixed(0)}%</span>
                </div>
            )}
            <div className="w-full bg-slate-100 rounded-full h-2.5 overflow-hidden">
                <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${pct}%` }}
                    transition={{ duration: 1.2, delay: 0.2, ease: "easeOut" }}
                    className={`h-full rounded-full ${color} relative`}>
                    {pct > 15 && (
                        <div className="absolute inset-0 bg-white/20 rounded-full animate-pulse" />
                    )}
                </motion.div>
            </div>
        </div>
    );
}

// ============================================================
// PODIUM CARD (3D Podium View)
// ============================================================

// ============================================================
// REDESIGNED PODIUM CARD (More Professional Look)
// ============================================================

function PodiumCard({ performer, rank, maxSales }: { performer: StaffPerformanceMetrics; rank: number; maxSales: number }) {
    const sales = parseFloat(performer.totalSales || '0');
    const pctOfMax = maxSales > 0 ? Math.round((sales / maxSales) * 100) : 0;

    const podiumConfig = {
        1: {
            height: 'h-40',
            color: 'from-amber-400 via-yellow-500 to-amber-500',
            bg: 'from-amber-50 via-yellow-50 to-amber-50',
            border: 'border-amber-300',
            shadow: 'shadow-amber-200/30',
            emoji: '🥇',
            label: '1st Place',
            avatarGradient: 'from-amber-400 to-yellow-600',
            avatarRing: 'ring-amber-300',
            barColor: 'bg-gradient-to-r from-amber-400 to-yellow-500',
            nameColor: 'text-amber-700',
        },
        2: {
            height: 'h-32',
            color: 'from-slate-400 via-gray-400 to-slate-500',
            bg: 'from-slate-50 via-gray-50 to-slate-50',
            border: 'border-slate-300',
            shadow: 'shadow-slate-200/30',
            emoji: '🥈',
            label: '2nd Place',
            avatarGradient: 'from-slate-400 to-gray-500',
            avatarRing: 'ring-slate-300',
            barColor: 'bg-gradient-to-r from-slate-400 to-gray-500',
            nameColor: 'text-slate-700',
        },
        3: {
            height: 'h-28',
            color: 'from-amber-600 via-orange-600 to-amber-700',
            bg: 'from-amber-50/50 via-orange-50/30 to-amber-50/50',
            border: 'border-amber-500/30',
            shadow: 'shadow-amber-200/20',
            emoji: '🥉',
            label: '3rd Place',
            avatarGradient: 'from-amber-600 to-orange-700',
            avatarRing: 'ring-amber-500/30',
            barColor: 'bg-gradient-to-r from-amber-600 to-orange-700',
            nameColor: 'text-amber-800',
        },
    };

    const config = podiumConfig[rank as keyof typeof podiumConfig];

    return (
        <motion.div
            initial={{ opacity: 0, y: 60 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: rank * 0.2, type: "spring", stiffness: 80, damping: 12 }}
            whileHover={{ y: -12, scale: 1.04 }}
            className="flex flex-col items-center cursor-pointer group w-40 md:w-48 lg:w-56">

            {/* Avatar Section */}
            <div className="relative mb-3">
                <motion.div
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    className={clsx(
                        'w-20 h-20 md:w-24 md:h-24 rounded-3xl flex items-center justify-center text-white text-2xl md:text-3xl font-black shadow-2xl transition-shadow',
                        `bg-gradient-to-br ${config.avatarGradient}`,
                        `ring-4 ${config.avatarRing} ring-offset-4 ring-offset-white`
                    )}>
                    {performer.staffName?.charAt(0) || '?'}
                </motion.div>
                {/* Rank Badge */}
                <div className="absolute -top-2 -right-2 bg-white rounded-full p-1 shadow-lg">
                    <span className="text-xl">{config.emoji}</span>
                </div>
                {/* Crown sparkle for 1st */}
                {rank === 1 && (
                    <>
                        <Sparkles size={12} className="absolute -top-4 -left-2 text-amber-400 animate-pulse" />
                        <Sparkles size={10} className="absolute -top-3 right-8 text-yellow-400 animate-pulse delay-300" />
                    </>
                )}
            </div>

            {/* Name & Role */}
            <p className={clsx('font-black text-sm md:text-base text-center max-w-[140px] truncate', config.nameColor)}>
                {performer.staffName}
            </p>
            <Badge variant="neutral" size="sm" className="mt-1">{performer.role || 'Staff'}</Badge>

            {/* Revenue */}
            <p className="text-lg md:text-xl font-black text-emerald-600 mt-2">
                {formatNaira(performer.totalSales)}
            </p>

            {/* Stats Row */}
            <div className="flex items-center gap-3 mt-2 text-[10px] md:text-xs text-slate-500">
                <span className="flex items-center gap-1">
                    <ShoppingCart size={10} /> {performer.totalTransactions}
                </span>
                <span className="flex items-center gap-1">
                    <TrendingUp size={10} /> {formatNaira(performer.averageBasket)}
                </span>
            </div>

            {/* Performance Bar */}
            <div className="w-full mt-3 px-2">
                <div className="flex justify-between text-[9px] text-slate-400 mb-1">
                    <span>Performance</span>
                    <span className="font-bold">{pctOfMax}%</span>
                </div>
                <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden">
                    <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${pctOfMax}%` }}
                        transition={{ duration: 1.5, delay: 0.5, ease: "easeOut" }}
                        className={clsx('h-full rounded-full relative', config.barColor)}>
                        <div className="absolute inset-0 bg-white/20 rounded-full animate-pulse" />
                    </motion.div>
                </div>
            </div>

            {/* Podium Stand */}
            <div className={clsx(
                'w-full rounded-t-2xl mt-4 flex flex-col items-center justify-center text-white font-black shadow-xl transition-all',
                config.height,
                `bg-gradient-to-b ${config.color}`
            )}>
                <span className="text-2xl md:text-3xl drop-shadow-lg">{config.emoji}</span>
                <span className="text-[10px] md:text-xs font-medium mt-1 opacity-80">{config.label}</span>
            </div>
        </motion.div>
    );
}

// ============================================================
// PERFORMER CARD (Grid View)
// ============================================================

function PerformerCard({ performer, rank, maxSales }: { performer: StaffPerformanceMetrics; rank: number; maxSales: number }) {
    const sales = parseFloat(performer.totalSales || '0');
    const isTop3 = rank >= 1 && rank <= 3;

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: rank * 0.03 }}
            whileHover={{ y: -4, scale: 1.01 }}
            onClick={() => window.location.href = `/staff/${performer.staffId}`}
            className={clsx(
                'relative overflow-hidden rounded-2xl border-2 p-5 transition-all duration-300 cursor-pointer group',
                isTop3
                    ? rank === 1
                        ? 'border-amber-300 bg-gradient-to-br from-amber-50 via-yellow-50 to-white shadow-lg shadow-amber-200/20'
                        : rank === 2
                            ? 'border-slate-300 bg-gradient-to-br from-slate-50 via-gray-50 to-white shadow-lg shadow-slate-200/20'
                            : 'border-amber-500/30 bg-gradient-to-br from-amber-50/50 via-orange-50/30 to-white shadow-lg shadow-amber-200/10'
                    : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-lg'
            )}>

            {/* Background decoration for top 3 */}
            {isTop3 && (
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-amber-100/20 to-transparent rounded-bl-full" />
            )}

            <div className="relative">
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                        <div className={clsx(
                            'w-12 h-12 rounded-xl flex items-center justify-center text-white text-lg font-bold shadow-lg',
                            rank === 1 ? 'bg-gradient-to-br from-amber-400 to-yellow-600' :
                                rank === 2 ? 'bg-gradient-to-br from-slate-400 to-gray-600' :
                                    rank === 3 ? 'bg-gradient-to-br from-amber-600 to-orange-700' :
                                        'bg-gradient-to-br from-emerald-500 to-teal-600'
                        )}>
                            {performer.staffName?.charAt(0) || '?'}
                        </div>
                        <div className="min-w-0">
                            <h3 className="font-bold text-slate-900 truncate">{performer.staffName}</h3>
                            <div className="flex items-center gap-2 mt-0.5">
                                <Badge variant="neutral" size="sm">{performer.role || 'Staff'}</Badge>
                                {performer.branchName && (
                                    <span className="text-[10px] text-slate-400 flex items-center gap-1">
                                        <Store size={9} />{performer.branchName}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                    <RankBadge rank={rank} />
                </div>

                {/* Performance Bar */}
                <PerformanceBar
                    value={sales}
                    max={maxSales}
                    color={rank === 1 ? 'bg-gradient-to-r from-amber-400 to-yellow-500' : 'bg-gradient-to-r from-emerald-500 to-teal-600'}
                    label="Revenue"
                />

                {/* Metrics Grid */}
                <div className="grid grid-cols-3 gap-3 mt-4 pt-4 border-t border-slate-100">
                    <div className="text-center">
                        <p className="text-sm font-black text-emerald-600">{formatNaira(performer.totalSales)}</p>
                        <p className="text-[9px] text-slate-400 uppercase font-bold tracking-wider">Revenue</p>
                    </div>
                    <div className="text-center">
                        <p className="text-sm font-black text-blue-600">{formatNumber(performer.totalTransactions)}</p>
                        <p className="text-[9px] text-slate-400 uppercase font-bold tracking-wider">Sales #</p>
                    </div>
                    <div className="text-center">
                        <p className="text-sm font-black text-purple-600">{formatNaira(performer.averageBasket)}</p>
                        <p className="text-[9px] text-slate-400 uppercase font-bold tracking-wider">Avg</p>
                    </div>
                </div>

                {/* Additional Metrics */}
                <div className="grid grid-cols-2 gap-2 mt-3">
                    <div className="flex items-center gap-1.5 text-[10px] text-slate-500">
                        <Clock size={10} />
                        <span>{performer.totalHours}h / {performer.totalShifts} shifts</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-[10px] text-slate-500 justify-end">
                        <TrendingUpIcon size={10} className="text-emerald-500" />
                        <span>{formatNaira(performer.salesPerHour)}/hr</span>
                    </div>
                </div>
            </div>
        </motion.div>
    );
}

// ============================================================
// MAIN COMPONENT
// ============================================================

export function StaffPerformancePage() {
    const navigate = useNavigate();
    const tenantId = useAuthStore(s => s.user?.tenantId);

    const [viewMode, setViewMode] = useState<'podium' | 'grid' | 'table'>('podium');
    const [page, setPage] = useState(1);
    const [filterBusiness, setFilterBusiness] = useState('');
    const [sortBy, setSortBy] = useState('totalSales');
    const [order, setOrder] = useState<'asc' | 'desc'>('desc');
    const [searchTerm, setSearchTerm] = useState('');
    const limit = 12;

    // ==================== QUERIES ====================

    const { data: businesses } = useQuery<Business[]>({
        queryKey: ['businesses-active', tenantId],
        queryFn: businessApi.getBusinessesWithStats,
        enabled: !!tenantId,
    });

    const { data: performanceData, isLoading, isError, refetch } = useQuery<PerformanceResponse>({
        queryKey: ['staff-performance', tenantId, filterBusiness, sortBy, order],
        queryFn: () => staffApi.getStaffPerformance(tenantId!, {
            businessId: filterBusiness || undefined,
            sortBy, order, limit: 100,
        }),
        enabled: !!tenantId,
    });

    // ==================== DERIVED DATA ====================

    const allPerformers = useMemo(() => {
        const data = performanceData?.performance || [];
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            return data.filter(p =>
                p.staffName.toLowerCase().includes(term) ||
                p.role.toLowerCase().includes(term) ||
                (p.branchName && p.branchName.toLowerCase().includes(term))
            );
        }
        return data;
    }, [performanceData, searchTerm]);

    const summary = performanceData?.summary;
    const maxSales = allPerformers.length > 0 ? Math.max(...allPerformers.map(p => parseFloat(p.totalSales || '0'))) : 0;

    // Pagination
    const totalCount = allPerformers.length;
    const totalPages = Math.ceil(totalCount / limit);
    const paginatedPerformers = allPerformers.slice((page - 1) * limit, page * limit);
    const top3 = maxSales > 0 ? allPerformers.filter(p => parseFloat(p.totalSales || '0') > 0).slice(0, 3) : [];

    // ==================== LOADING / ERROR ====================

    if (isLoading) return (
        <div className="flex items-center justify-center min-h-[70vh]">
            <div className="text-center space-y-4">
                <Spinner size="xl" variant="amber" />
                <p className="text-sm text-slate-400 animate-pulse">Crunching performance numbers...</p>
            </div>
        </div>
    );

    if (isError) return (
        <div className="min-h-screen flex items-center justify-center p-4">
            <ErrorState
                title="Failed to load performance data"
                message="There was an error loading the performance metrics."
                onRetry={() => refetch()}
            />
        </div>
    );

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-amber-50/30">
            <div className="max-w-[1600px] mx-auto p-4 lg:p-6 space-y-5">

                {/* ========== HEADER ========== */}
                <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
                    <button
                        onClick={() => navigate('/staff')}
                        className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-amber-600 mb-3 group transition-colors">
                        <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
                        Back to Staff Management
                    </button>

                    <div className="relative overflow-hidden bg-gradient-to-br from-amber-500 via-amber-600 to-orange-700 rounded-3xl shadow-2xl">
                        <div className="absolute inset-0">
                            <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(255,255,255,0.15),transparent_50%)]" />
                            <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-3xl" />
                        </div>
                        <div className="relative px-6 lg:px-10 py-8 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
                            <div className="flex items-center gap-4">
                                <div className="w-16 h-16 rounded-2xl bg-white/15 backdrop-blur-sm ring-1 ring-white/20 flex items-center justify-center">
                                    <Trophy size={32} className="text-white" />
                                </div>
                                <div>
                                    <h1 className="text-3xl lg:text-4xl font-black text-white">Staff Performance</h1>
                                    <p className="text-amber-100 text-sm mt-1">
                                        {maxSales > 0 && summary?.topPerformer && summary.topPerformer !== 'No sales recorded yet'
                                            ? `${allPerformers.length} staff members ranked • 🏆 ${summary.topPerformer} leads`
                                            : `${allPerformers.length} team members • Rankings activate as sales or shifts are recorded`}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </motion.div>

                {/* ========== SUMMARY METRICS ========== */}
                {summary && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {[
                            { label: 'Total Staff', value: summary.totalStaff, icon: Users, gradient: 'from-emerald-500 to-teal-600' },
                            { label: 'Total Revenue', value: formatNaira(summary.totalSales), icon: DollarSign, gradient: 'from-blue-500 to-indigo-600' },
                            { label: 'Transactions', value: formatNumber(summary.totalTransactions), icon: ShoppingCart, gradient: 'from-purple-500 to-violet-600' },
                            { label: 'Top Performer', value: summary.topPerformer, sub: formatNaira(summary.topPerformerSales), icon: Crown, gradient: 'from-amber-500 to-yellow-600' },
                        ].map((s, i) => (
                            <motion.div
                                key={s.label}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: i * 0.08 }}
                                className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 hover:shadow-md transition-all">
                                <div className={`p-2.5 rounded-xl bg-gradient-to-br ${s.gradient} shadow-md w-fit mb-3`}>
                                    <s.icon className="w-4 h-4 text-white" />
                                </div>
                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{s.label}</p>
                                <p className="text-xl font-bold text-slate-900 mt-0.5 truncate">{s.value}</p>
                                {'sub' in s && s.sub && (
                                    <p className="text-xs text-emerald-600 font-medium mt-0.5">{s.sub}</p>
                                )}
                            </motion.div>
                        ))}
                    </div>
                )}

                {/* ========== TOOLBAR ========== */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 bg-white rounded-2xl border border-slate-200 shadow-sm p-3">
                    <div className="flex items-center gap-2 flex-wrap">
                        {/* View Mode Toggle */}
                        <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1">
                            {[
                                { mode: 'podium' as const, icon: Trophy, label: 'Podium' },
                                { mode: 'grid' as const, icon: LayoutGrid, label: 'Grid' },
                                { mode: 'table' as const, icon: List, label: 'Table' },
                            ].map(({ mode, icon: Icon, label }) => (
                                <button
                                    key={mode}
                                    onClick={() => setViewMode(mode)}
                                    className={clsx(
                                        'p-2 rounded-md transition-all flex items-center gap-1.5',
                                        viewMode === mode
                                            ? 'bg-white shadow-sm text-amber-600'
                                            : 'text-slate-400 hover:text-slate-600'
                                    )}>
                                    <Icon size={16} />
                                    <span className="text-xs font-medium hidden sm:inline">{label}</span>
                                </button>
                            ))}
                        </div>

                        {/* Search */}
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                            <input
                                type="text"
                                placeholder="Search staff..."
                                value={searchTerm}
                                onChange={e => { setSearchTerm(e.target.value); setPage(1); }}
                                className="pl-8 pr-8 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs w-36 focus:ring-2 focus:ring-amber-500/20 focus:border-amber-400"
                            />
                            {searchTerm && (
                                <button onClick={() => setSearchTerm('')} className="absolute right-2 top-1/2 -translate-y-1/2">
                                    <X size={12} className="text-slate-400 hover:text-slate-600" />
                                </button>
                            )}
                        </div>

                        {/* Business Filter */}
                        <select
                            value={filterBusiness}
                            onChange={e => { setFilterBusiness(e.target.value); setPage(1); }}
                            className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-600">
                            <option value="">All Businesses</option>
                            {(businesses || []).map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                        </select>
                    </div>

                    <div className="flex items-center gap-2">
                        {/* Sort */}
                        <select
                            value={sortBy}
                            onChange={e => setSortBy(e.target.value)}
                            className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-600">
                            <option value="totalSales">By Revenue</option>
                            <option value="totalTransactions">By Transactions</option>
                            <option value="averageBasket">By Avg Basket</option>
                            <option value="salesPerHour">By Sales/Hour</option>
                        </select>
                        <button
                            onClick={() => setOrder(o => o === 'asc' ? 'desc' : 'asc')}
                            className={clsx(
                                'p-2 rounded-lg hover:bg-slate-50 transition-colors',
                                order === 'desc' ? 'text-amber-600' : 'text-slate-400'
                            )}>
                            <ArrowUpDown size={14} />
                        </button>
                    </div>
                </div>

                {/* ========== CONTENT ========== */}
                {allPerformers.length === 0 ? (
                    <EmptyState
                        icon={<Trophy size={48} />}
                        title="No performance data yet"
                        description="Staff performance will appear here once sales are recorded."
                        action={{ label: 'Go to Staff', onClick: () => navigate('/staff') }}
                    />
                ) : (
                    <>
                        {/* PODIUM VIEW */}
                        {viewMode === 'podium' && (
                            <div className="space-y-6">
                                {/* Top 3 Podium only if sales > 0 */}
                                {maxSales > 0 && top3.length > 0 ? (
                                    <div className="bg-white rounded-3xl border border-slate-200 shadow-xl p-6 lg:p-10">
                                        <div className="text-center mb-10">
                                            <motion.div
                                                initial={{ scale: 0 }}
                                                animate={{ scale: 1 }}
                                                transition={{ type: "spring", stiffness: 200 }}
                                                className="inline-flex items-center gap-3 mb-3">
                                                <Sparkles size={24} className="text-amber-400" />
                                                <Trophy size={36} className="text-amber-500" />
                                                <Sparkles size={24} className="text-amber-400" />
                                            </motion.div>
                                            <h2 className="text-2xl md:text-3xl font-black text-slate-800">Top Performers</h2>
                                            <p className="text-sm text-slate-400 mt-2">Leading the leaderboard this period</p>
                                        </div>
                                        <div className="flex items-end justify-center gap-4 md:gap-6 lg:gap-10">
                                            {/* 2nd Place - Left */}
                                            {top3[1] && <PodiumCard performer={top3[1]} rank={2} maxSales={maxSales} />}
                                            {/* 1st Place - Center (taller) */}
                                            {top3[0] && <PodiumCard performer={top3[0]} rank={1} maxSales={maxSales} />}
                                            {/* 3rd Place - Right */}
                                            {top3[2] && <PodiumCard performer={top3[2]} rank={3} maxSales={maxSales} />}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-8 text-center space-y-3">
                                        <div className="w-14 h-14 rounded-2xl bg-amber-50 text-amber-600 flex items-center justify-center mx-auto shadow-inner">
                                            <Trophy size={28} />
                                        </div>
                                        <h3 className="text-lg font-bold text-slate-800">No Sales Recorded Yet</h3>
                                        <p className="text-sm text-slate-500 max-w-md mx-auto">
                                            Staff rankings and top performer podium will automatically activate once sales transactions or shifts are recorded in the system.
                                        </p>
                                    </div>
                                )}

                                {/* Sales Performance Records Table */}
                                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                                    <div className="p-5 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                                        <div>
                                            <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                                                <TrendingUp size={16} className="text-emerald-500" />
                                                Sales Performance Records
                                            </h3>
                                            <p className="text-xs text-slate-400 mt-0.5">
                                                {maxSales > 0 ? 'Individual sales revenue, transaction volume, and ticket averages based on filters' : 'Authorized staff members awaiting sales activity'}
                                            </p>
                                        </div>
                                        <Badge variant="neutral" size="sm" className="w-fit">
                                            {allPerformers.length} staff member{allPerformers.length !== 1 ? 's' : ''}
                                        </Badge>
                                    </div>

                                    <div className="overflow-x-auto">
                                        <table className="min-w-full divide-y divide-slate-100">
                                            <thead>
                                                <tr className="bg-slate-50/70">
                                                    <th className="px-6 py-3.5 text-center text-[10px] font-bold text-slate-500 uppercase tracking-wider w-16">Rank</th>
                                                    <th className="px-6 py-3.5 text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider">Staff Member</th>
                                                    <th className="px-6 py-3.5 text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider hidden md:table-cell">Role</th>
                                                    <th className="px-6 py-3.5 text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider hidden lg:table-cell">Branch</th>
                                                    <th className="px-6 py-3.5 text-right text-[10px] font-bold text-slate-500 uppercase tracking-wider">Revenue</th>
                                                    <th className="px-6 py-3.5 text-right text-[10px] font-bold text-slate-500 uppercase tracking-wider hidden sm:table-cell">Sales</th>
                                                    <th className="px-6 py-3.5 text-right text-[10px] font-bold text-slate-500 uppercase tracking-wider hidden md:table-cell">Avg Basket</th>
                                                    <th className="px-6 py-3.5 text-right text-[10px] font-bold text-slate-500 uppercase tracking-wider hidden lg:table-cell">Shifts</th>
                                                    <th className="px-6 py-3.5 text-right text-[10px] font-bold text-slate-500 uppercase tracking-wider">Per Hour</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-slate-50">
                                                {allPerformers.map((p) => (
                                                    <motion.tr
                                                        key={p.staffId}
                                                        onClick={() => navigate(`/staff/${p.staffId}`)}
                                                        className={clsx(
                                                            'hover:bg-slate-50 cursor-pointer transition-colors group',
                                                            p.rank >= 1 && p.rank <= 3 && 'bg-amber-50/20 hover:bg-amber-50/40'
                                                        )}>
                                                        <td className="px-6 py-3.5 text-center">
                                                            <RankBadge rank={p.rank} />
                                                        </td>
                                                        <td className="px-6 py-3.5">
                                                            <div className="flex items-center gap-3">
                                                                <div className={clsx(
                                                                    'w-9 h-9 rounded-xl flex items-center justify-center text-white text-xs font-bold shadow-xs',
                                                                    p.rank === 1 ? 'bg-gradient-to-br from-amber-400 to-yellow-600' :
                                                                        p.rank === 2 ? 'bg-gradient-to-br from-slate-400 to-gray-600' :
                                                                            p.rank === 3 ? 'bg-gradient-to-br from-amber-600 to-orange-700' :
                                                                                'bg-gradient-to-br from-emerald-500 to-teal-600'
                                                                )}>
                                                                    {p.staffName?.charAt(0) || '?'}
                                                                </div>
                                                                <div className="min-w-0">
                                                                    <p className="text-sm font-semibold text-slate-900 group-hover:text-emerald-600 transition-colors truncate">
                                                                        {p.staffName}
                                                                    </p>
                                                                    <p className="text-[10px] text-slate-400 font-mono">
                                                                        {p.staffId.slice(0, 8)}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-3.5 hidden md:table-cell">
                                                            <Badge variant="neutral" size="sm">{p.role || 'Staff'}</Badge>
                                                        </td>
                                                        <td className="px-6 py-3.5 text-xs text-slate-600 hidden lg:table-cell">
                                                            {p.branchName || '—'}
                                                        </td>
                                                        <td className="px-6 py-3.5 text-right font-bold text-emerald-600 text-sm">
                                                            {formatNaira(p.totalSales)}
                                                        </td>
                                                        <td className="px-6 py-3.5 text-right text-xs text-slate-600 hidden sm:table-cell font-medium">
                                                            {p.totalTransactions.toLocaleString()}
                                                        </td>
                                                        <td className="px-6 py-3.5 text-right text-xs text-slate-600 hidden md:table-cell">
                                                            {formatNaira(p.averageBasket)}
                                                        </td>
                                                        <td className="px-6 py-3.5 text-right text-xs text-slate-600 hidden lg:table-cell">
                                                            {p.totalShifts}
                                                        </td>
                                                        <td className="px-6 py-3.5 text-right text-xs text-slate-600 font-medium">
                                                            {formatNaira(p.salesPerHour)}/h
                                                        </td>
                                                    </motion.tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* GRID VIEW */}
                        {viewMode === 'grid' && (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                                {paginatedPerformers.map(p => (
                                    <PerformerCard key={p.staffId} performer={p} rank={p.rank} maxSales={maxSales} />
                                ))}
                            </div>
                        )}

                        {/* TABLE VIEW */}
                        {viewMode === 'table' && (
                            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-slate-100">
                                        <thead>
                                            <tr className="bg-slate-50">
                                                <th className="px-6 py-4 text-center text-[10px] font-bold text-slate-500 uppercase tracking-wider w-16">Rank</th>
                                                <th className="px-6 py-4 text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider">Staff Member</th>
                                                <th className="px-6 py-4 text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider hidden md:table-cell">Role</th>
                                                <th className="px-6 py-4 text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider hidden lg:table-cell">Branch</th>
                                                <th className="px-6 py-4 text-right text-[10px] font-bold text-slate-500 uppercase tracking-wider">Revenue</th>
                                                <th className="px-6 py-4 text-right text-[10px] font-bold text-slate-500 uppercase tracking-wider hidden sm:table-cell">Sales</th>
                                                <th className="px-6 py-4 text-right text-[10px] font-bold text-slate-500 uppercase tracking-wider hidden md:table-cell">Avg Basket</th>
                                                <th className="px-6 py-4 text-right text-[10px] font-bold text-slate-500 uppercase tracking-wider hidden lg:table-cell">Shifts</th>
                                                <th className="px-6 py-4 text-right text-[10px] font-bold text-slate-500 uppercase tracking-wider">Per Hour</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-50">
                                            {paginatedPerformers.map((p) => (
                                                <motion.tr
                                                    key={p.staffId}
                                                    initial={{ opacity: 0 }}
                                                    animate={{ opacity: 1 }}
                                                    onClick={() => navigate(`/staff/${p.staffId}`)}
                                                    className={clsx(
                                                        'hover:bg-slate-50 cursor-pointer transition-colors group',
                                                        p.rank >= 1 && p.rank <= 3 && 'bg-amber-50/30 hover:bg-amber-50/50'
                                                    )}>
                                                    <td className="px-6 py-4 text-center">
                                                        <RankBadge rank={p.rank} />
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <div className="flex items-center gap-3">
                                                            <div className={clsx(
                                                                'w-10 h-10 rounded-xl flex items-center justify-center text-white text-sm font-bold shadow-md',
                                                                p.rank === 1 ? 'bg-gradient-to-br from-amber-400 to-yellow-600' :
                                                                    p.rank === 2 ? 'bg-gradient-to-br from-slate-400 to-gray-600' :
                                                                        p.rank === 3 ? 'bg-gradient-to-br from-amber-600 to-orange-700' :
                                                                            'bg-gradient-to-br from-emerald-500 to-teal-600'
                                                            )}>
                                                                {p.staffName?.charAt(0) || '?'}
                                                            </div>
                                                            <div>
                                                                <p className="text-sm font-bold text-slate-900 group-hover:text-amber-600 transition-colors">
                                                                    {p.staffName}
                                                                </p>
                                                                {p.rank <= 3 && (
                                                                    <p className="text-[10px] text-amber-600 font-medium">
                                                                        Top {p.rank} Performer
                                                                    </p>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 hidden md:table-cell">
                                                        <Badge variant="neutral" size="sm">{p.role}</Badge>
                                                    </td>
                                                    <td className="px-6 py-4 hidden lg:table-cell">
                                                        <span className="text-xs text-slate-500 flex items-center gap-1">
                                                            <Store size={10} />
                                                            {p.branchName || '-'}
                                                        </span>
                                                    </td>
                                                    <td className="px-6 py-4 text-right">
                                                        <span className="text-sm font-bold text-emerald-600">
                                                            {formatNaira(p.totalSales)}
                                                        </span>
                                                    </td>
                                                    <td className="px-6 py-4 text-right text-sm font-medium text-slate-600 hidden sm:table-cell">
                                                        {formatNumber(p.totalTransactions)}
                                                    </td>
                                                    <td className="px-6 py-4 text-right text-sm font-medium text-slate-600 hidden md:table-cell">
                                                        {formatNaira(p.averageBasket)}
                                                    </td>
                                                    <td className="px-6 py-4 text-right text-sm font-medium text-slate-600 hidden lg:table-cell">
                                                        {p.totalShifts}
                                                    </td>
                                                    <td className="px-6 py-4 text-right">
                                                        <span className="text-sm font-bold text-purple-600">
                                                            {formatNaira(p.salesPerHour)}
                                                        </span>
                                                    </td>
                                                </motion.tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {/* PAGINATION */}
                        {totalPages > 1 && (
                            <div className="flex items-center justify-between bg-white rounded-2xl border border-slate-200 shadow-sm px-6 py-4">
                                <p className="text-sm text-slate-500">
                                    Showing {((page - 1) * limit) + 1}-{Math.min(page * limit, totalCount)} of {totalCount} staff
                                </p>
                                <div className="flex items-center gap-1">
                                    <button
                                        onClick={() => setPage(p => Math.max(1, p - 1))}
                                        disabled={page === 1}
                                        className="p-2 rounded-lg hover:bg-slate-50 disabled:opacity-30 transition-colors">
                                        <ChevronLeft size={16} />
                                    </button>
                                    {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                                        let pageNum: number;
                                        if (totalPages <= 5) {
                                            pageNum = i + 1;
                                        } else if (page <= 3) {
                                            pageNum = i + 1;
                                        } else if (page >= totalPages - 2) {
                                            pageNum = totalPages - 4 + i;
                                        } else {
                                            pageNum = page - 2 + i;
                                        }
                                        return (
                                            <button
                                                key={pageNum}
                                                onClick={() => setPage(pageNum)}
                                                className={clsx(
                                                    'w-9 h-9 rounded-lg text-sm font-medium transition-all',
                                                    page === pageNum
                                                        ? 'bg-amber-500 text-white shadow-md'
                                                        : 'text-slate-600 hover:bg-slate-50'
                                                )}>
                                                {pageNum}
                                            </button>
                                        );
                                    })}
                                    <button
                                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                        disabled={page === totalPages}
                                        className="p-2 rounded-lg hover:bg-slate-50 disabled:opacity-30 transition-colors">
                                        <ChevronRight size={16} />
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}